import { useState } from "react";
import useEffectCallback from "../../util/useEffectCallback";
import { SheetProvider } from "./Context";

type SheetProps = {
    children: React.ReactNode;
    initialOpen?: boolean;
    ignoreModals?: boolean;
};

export const Sheet = ({ children, initialOpen, ignoreModals }: SheetProps) => {
    const [id] = useState(() => createSheetKey());
    const [isSheetPresent, setSheetPresent] = useState(initialOpen ?? false);

    return (
        <SheetProvider value={{ isSheetPresent, setSheetPresent, id }}>
            {isSheetPresent && (
                <>
                    <KeyboardListener
                        setSheetPresent={setSheetPresent}
                        id={id}
                        ignoreModals={ignoreModals}
                    />
                    <MouseDownListener
                        id={id}
                        setSheetPresent={setSheetPresent}
                        ignoreModals={ignoreModals}
                    />
                </>
            )}
            {children}
        </SheetProvider>
    );
};

const MouseDownListener = ({
    id,
    setSheetPresent,
    ignoreModals = false,
}: {
    id: string;
    setSheetPresent: (value: boolean | ((prev: boolean) => boolean)) => void;
    ignoreModals?: boolean;
}) => {
    useEffectCallback(
        (id) => {
            const handleClick = (e: MouseEvent) => {
                const sheet = document.querySelector(`[data-sheet="${id}"]`);
                const target = e.target as Node;

                if (ignoreModals) {
                    const modal = (target as Element).closest?.(
                        '[data-modal="true"]',
                    );
                    if (modal) return;
                }

                if (sheet && !sheet.contains(target)) {
                    setSheetPresent(false);
                }
            };
            document.addEventListener("mousedown", handleClick, true);
            return () => {
                document.removeEventListener("mousedown", handleClick, true);
            };
        },
        [id],
    );
    return null;
};

const KeyboardListener = ({
    id,
    setSheetPresent,
    ignoreModals = false,
}: {
    id: string;
    setSheetPresent: (value: boolean | ((prev: boolean) => boolean)) => void;
    ignoreModals?: boolean;
}) => {
    useEffectCallback(() => {
        const handleDismiss = (e: KeyboardEvent) => {
            if (ignoreModals) {
                const modal = (e.target as Element).closest?.(
                    '[data-modal="true"]',
                );
                if (modal) return;
            }

            const sheet = document.querySelector(`[data-sheet="${id}"]`);
            if (e.key === " " && sheet && !sheet.contains(e.target as Node)) {
                setSheetPresent(false);
            } else if (e.key === "Escape") {
                setSheetPresent(false);
            }
        };
        window.addEventListener("keydown", handleDismiss);
        return () => {
            window.removeEventListener("keydown", handleDismiss);
        };
    }, []);

    return null;
};

const createSheetKey = () => {
    let key = Math.random().toString(36).slice(2, 10);
    const dataProperty = `data-sheet="${key}"`;
    while (document.querySelector(`[${dataProperty}]`)) {
        key = Math.random().toString(36).slice(2, 10);
    }
    return key;
};
